import React from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import { formatYearMonthDate } from 'utils'
import { Input } from 'lp-components'
import {
  NestedAttributesFieldArray,
  ReadOnlyCellInput,
  DateInput,
  ReadOnlyCellInputWithTooltip,
} from 'components'

const propTypes = {
  flashErrorMessage: PropTypes.func.isRequired,
  credentials: PropTypes.array,
  change: PropTypes.func.isRequired,
}

const defaultProps = {}

function EducationRow({
  fields,
  index,
  change,
  credentials,
  flashErrorMessage,
}) {
  const field = fields.name ? `${fields.name}[${index}]` : `${index}`
  const credential = fields.get(index)

  if (credential.credentialType !== 'degree') {
    return null
  }

  const isEditing = credential?.isEditing

  const setIsEditing = (isEditing) => {
    const canSave =
      credential.institutionName &&
      credential.credentialName &&
      credential.fieldOfStudyCourseName &&
      credential.issueDate
    if (!isEditing) {
      if (!canSave) {
        flashErrorMessage('Please fill in all fields before saving.')
        return
      }
    }
    const updatedCredential = { ...credential, isEditing }

    const updatedCredentials = [...credentials]
    updatedCredentials[index] = updatedCredential
    change('credentials', updatedCredentials)
  }

  return (
    <div key={credential.id || index} className="form-table-row">
      <div className="form-table-cell">
        <Field
          name={field + '.institutionName'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="Enter school name"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.credentialName'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="e.g., Bachelor's, Master's"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.fieldOfStudyCourseName'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="e.g., Computer Science"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.issueDate'}
          label={false}
          component={isEditing ? DateInput : ReadOnlyCellInput}
          placeholderText="MM/DD/YYYY"
          popperPlacement="top"
          className="label-block"
          format={formatYearMonthDate}
          parse={formatYearMonthDate}
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.description'}
          label={false}
          placeholder="Describe your education experience"
          component={isEditing ? Input : ReadOnlyCellInputWithTooltip}
          showTooltip={!isEditing}
          tooltipContent={credential.description}
        />
      </div>
      <div className="form-table-cell">
        <button
          type="button"
          className="button-primary"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? 'Save' : 'Edit'}
        </button>
      </div>
      <div className="form-table-cell">
        <button
          type="button"
          className="remove-row"
          aria-label={`Remove ${
            credential.institutionName || 'education'
          } education entry`}
          onClick={() => fields.destroy(index)}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  )
}

function EducationFields({ fields, change, credentials, flashErrorMessage }) {
  return (
    <>
      {fields.map((_, i) => {
        return (
          <EducationRow
            key={`credential-${i}`}
            index={i}
            fields={fields}
            change={change}
            credentials={credentials}
            flashErrorMessage={flashErrorMessage}
          />
        )
      })}
    </>
  )
}

function EducationSection({ change, credentials, flashErrorMessage }) {
  const addEducationEntry = () => {
    const newCredential = {
      institutionName: '',
      credentialType: 'degree',
      isEditing: true,
    }
    change('credentials', [...credentials, newCredential])
  }
  return (
    <section>
      <div>
        <div className="form-table">
          <div className="form-table-heading">
            <div className="form-table-row">
              <div className="form-table-head">School</div>
              <div className="form-table-head">Degree</div>
              <div className="form-table-head">Field of Study</div>
              <div className="form-table-head">End Date</div>
              <div className="form-table-head">Description</div>
              <div className="form-table-head" style={{ width: 70 }}></div>
              <div className="form-table-head" style={{ width: 20 }}></div>
            </div>
          </div>
          <div className="form-table-body">
            <NestedAttributesFieldArray
              name="credentials"
              component={EducationFields}
              change={change}
              credentials={credentials}
              flashErrorMessage={flashErrorMessage}
            />
          </div>
        </div>
      </div>
      <button type="button" className="link-black" onClick={addEducationEntry}>
        + Add New Education
      </button>
    </section>
  )
}

EducationSection.propTypes = propTypes
EducationSection.defaultProps = defaultProps

export default EducationSection
